import { useEffect, useState } from "react";
import api, { apiErrorMessage, formatDate, formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Trash, Wallet } from "lucide-react";

const STATUS_LABEL = { oczekuje: "Oczekuje", zaakceptowane: "Zaakceptowane", odrzucone: "Odrzucone" };

export default function AdminBudgetRequests() {
  const [list, setList] = useState([]);

  const reload = () => api.get("/budget-increase-requests").then((r) => setList(r.data));
  useEffect(() => { reload(); }, []);

  const deleteReq = async (id) => {
    if (!window.confirm("Usunąć prośbę o budżet? Tej operacji nie da się cofnąć.")) return;
    try {
      await api.delete(`/budget-increase-requests/${id}`);
      toast.success("Usunięto prośbę");
      reload();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader
        testId="admin-budget-reqs"
        title="Prośby o zwiększenie budżetu"
        subtitle="Wszystkie wnioski budowy NDI"
      />

      <div className="ol-card overflow-hidden hidden md:block">
        {list.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Brak wniosków.</div>
        ) : (
          <table className="ol-table" data-testid="admin-budget-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Budowa</th>
                <th>Pracownik</th>
                <th className="text-right">Obecny budżet</th>
                <th className="text-right">Proponowany</th>
                <th>Powód</th>
                <th>Status</th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td className="text-xs text-slate-600">{formatDate(r.created_at)}</td>
                  <td className="font-medium">{r.site_name || r.site_id}</td>
                  <td className="text-sm">{r.requested_by_name || r.requested_by}</td>
                  <td className="text-right font-mono text-sm">{formatPLN(r.current_budget)}</td>
                  <td className="text-right font-mono text-sm font-semibold text-[#CC4B00]">{formatPLN(r.requested_new_budget)}</td>
                  <td className="text-sm text-slate-600 max-w-xs">{r.reason || "—"}</td>
                  <td><span className="ol-badge bg-slate-100 text-slate-700 border border-slate-200">{STATUS_LABEL[r.status] || r.status}</span></td>
                  <td>
                    <button onClick={() => deleteReq(r.id)} data-testid={`delete-budget-${r.id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold">
                      <Trash className="w-3 h-3" /> Usuń
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {list.length === 0 ? (
          <div className="ol-card p-8 text-center text-sm text-slate-500">Brak wniosków.</div>
        ) : list.map((r) => (
          <div key={r.id} className="ol-card p-4" data-testid={`budget-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{formatDate(r.created_at)}</p>
                <p className="font-semibold text-slate-900 text-sm">{r.site_name || r.site_id}</p>
                <p className="text-xs text-slate-500">{r.requested_by_name}</p>
              </div>
              <span className="ol-badge bg-slate-100 text-slate-700 border border-slate-200 flex-shrink-0">{STATUS_LABEL[r.status] || r.status}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 py-2 border-t border-slate-100 mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Obecny</p>
                <p className="text-sm font-mono">{formatPLN(r.current_budget)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Proponowany</p>
                <p className="text-sm font-mono font-semibold text-[#CC4B00]">{formatPLN(r.requested_new_budget)}</p>
              </div>
            </div>
            {r.reason && <p className="text-xs text-slate-600 mb-2">{r.reason}</p>}
            <button onClick={() => deleteReq(r.id)} data-testid={`delete-budget-mobile-${r.id}`}
              className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold">
              <Trash className="w-3.5 h-3.5" /> Usuń wniosek
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
